import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Lightbulb,
  Database,
  Wrench,
  AppWindow,
  LayoutDashboard,
  Sparkles,
  Cpu,
  Plug,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/Button";
import { CtaBand } from "@/components/CtaBand";
import { VideoTile } from "@/components/VideoTile";
import { getJSON } from "@/lib/api";
import { VIDEO, POSTER } from "@/lib/images";
import { FORM_ENDPOINT } from "@/data/company";

const Label = ({ children, dark }) => (
  <span
    className={`text-xs font-semibold uppercase tracking-[0.16em] ${
      dark ? "text-[var(--mint)]" : "text-[var(--ember)]"
    }`}
  >
    {children}
  </span>
);

const WHO = [
  {
    icon: Lightbulb,
    t: "Founders with a product idea",
    d: "You know the problem cold. You need product direction and engineers who can ship it.",
  },
  {
    icon: Database,
    t: "Companies with proprietary data",
    d: "You're sitting on data nobody else has. We turn it into a product — including open-source LLMs tuned on it.",
  },
  {
    icon: Wrench,
    t: "Operators who outgrew tools",
    d: "Off-the-shelf software stopped fitting how you work. We build what does.",
  },
];

const BUILD = [
  { icon: AppWindow, t: "Custom applications", d: "Web and mobile products built around your users, not a template." },
  { icon: LayoutDashboard, t: "SaaS products", d: "Multi-tenant, billing, dashboards — built to sell, not to demo." },
  { icon: Sparkles, t: "AI-native products", d: "Products where the AI is the product, not a feature bolted on." },
  { icon: Cpu, t: "Open-source LLMs on your data", d: "Fine-tuned, self-hosted models trained on data only you have." },
  { icon: Database, t: "Data pipelines & infrastructure", d: "The plumbing that makes the product real at scale." },
  { icon: Plug, t: "Integrations & APIs", d: "Your product, connected to the tools your market already uses." },
];

const PHASES = [
  { n: "01", t: "Pitch", d: "You tell us the idea. We tell you honestly whether it's worth building.", tag: "Free call" },
  { n: "02", t: "Scope", d: "Product direction, architecture, timeline, and cost — written down before a line of code.", tag: "You approve the plan" },
  { n: "03", t: "Build", d: "We build in the open. You see it working, not just status updates.", tag: "Weekly demos" },
  { n: "04", t: "Launch", d: "We ship it — and if you want, we take it to market with you.", tag: "Go-to-market ready" },
];

const HANDOFF = [
  { t: "Product & Engineering builds it.", d: "Scoped, architected, shipped.", to: null },
  { t: "Marketing & Creative takes it to market.", d: "Launch, content, campaigns, demand.", to: "/growth" },
  { t: "Workflow & Automation runs it.", d: "Onboarding, support, and the ops behind the product.", to: "/workflow" },
];

const FAQ = [
  ["Who owns the product and the IP?", "You do. You own the code, the product, and the IP. We build it; it's yours."],
  ["Will you sign an NDA?", "Yes. Send it before the call if you'd like, or we'll send ours."],
  ["What if I only need scoping, not the build?", "That's a real engagement. We'll scope it, spec it, and hand you a plan you could build anywhere."],
  ["Can you work with my existing engineers?", "Yes — we can lead, embed, or fill the gaps."],
  ["What does it cost?", "It depends on scope. The pitch call is free, and the proposal is written before you commit to anything."],
  ["How long does a build take?", "Depends on scope. Most first versions ship in weeks, not quarters — the scope phase gives you a real timeline before you start."],
  ["Do you build with open-source models?", "Yes. If your edge is proprietary data, a fine-tuned open-source model you host and control is often the right answer."],
];

const leader = {
  name: "Muhammad Daud",
  title: "Product & Engineering lead",
  photo: `${process.env.PUBLIC_URL}/team/muhammad-daud.jpg`,
  bio: "Muhammad Daud leads the Product & Engineering team. He is a Full-Stack Developer with 8 years of experience in building modern web applications, AI-powered solutions, and business automation systems. He graduated from FAST – National University of Computer and Emerging Sciences with a degree in Computer Science.",
};

const inputCls =
  "w-full rounded-xl border border-black/10 bg-[var(--bg-base)] px-4 py-3 text-[15px] text-[var(--ink)] outline-none transition-colors focus:border-[var(--ember)] placeholder:text-[var(--muted)]/60";

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const PitchForm = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", idea: "", stage: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (!emailOk(form.email)) er.email = "Please enter a valid email.";
    if (!form.idea.trim()) er.idea = "Tell us what you want to build.";
    setErrors(er);
    if (Object.keys(er).length) return;
    setStatus("submitting");
    try {
      if (FORM_ENDPOINT) {
        await fetch(FORM_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...form, source: "product-pitch" }),
        });
      }
      setStatus("success");
    } catch {
      setStatus("success");
    }
  };

  if (status === "success")
    return (
      <div
        className="flex items-start gap-4 rounded-2xl border border-[var(--ember)]/30 bg-[var(--ember)]/[0.06] p-8"
        data-testid="pitch-success"
      >
        <CheckCircle2 className="mt-0.5 flex-shrink-0 text-[var(--ember)]" size={24} />
        <div>
          <p className="text-lg font-semibold">Pitch received.</p>
          <p className="mt-2 text-[15px] leading-relaxed text-[var(--muted)]">
            We'll be in touch to set up your pitch call — and we'll tell you honestly whether it's worth building.
          </p>
        </div>
      </div>
    );

  return (
    <form className="space-y-5" onSubmit={onSubmit} noValidate data-testid="pitch-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-2 block text-sm font-medium" htmlFor="pname">Name</label>
          <input id="pname" className={inputCls} value={form.name} onChange={set("name")} data-testid="pitch-name" />
          {errors.name && <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.name}</p>}
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium" htmlFor="pemail">Email</label>
          <input id="pemail" type="email" className={inputCls} value={form.email} onChange={set("email")} data-testid="pitch-email" />
          {errors.email && <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.email}</p>}
        </div>
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium" htmlFor="pcompany">Company (optional)</label>
        <input id="pcompany" className={inputCls} value={form.company} onChange={set("company")} data-testid="pitch-company" />
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium" htmlFor="pidea">Your idea, in one line</label>
        <input id="pidea" className={inputCls} value={form.idea} onChange={set("idea")} data-testid="pitch-idea" />
        {errors.idea && <p className="mt-1.5 text-[13px] text-[var(--ember)]">{errors.idea}</p>}
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium" htmlFor="pstage">What stage are you at?</label>
        <select id="pstage" className={inputCls} value={form.stage} onChange={set("stage")} data-testid="pitch-stage">
          <option value="">Select a stage</option>
          <option>Just an idea</option>
          <option>Have a spec or designs</option>
          <option>Have data, need a product</option>
          <option>Existing product to rebuild or extend</option>
        </select>
      </div>
      <Button type="submit" variant="primary" disabled={status === "submitting"} data-testid="pitch-submit">
        {status === "submitting" ? "Sending…" : "Pitch us your idea"}
      </Button>
    </form>
  );
};

export default function Product() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    getJSON("/projects")
      .then((rows) => setProjects((rows || []).filter((p) => (p.teams && p.teams.length ? p.teams : p.team ? [p.team] : []).includes("Product & Engineering")).slice(0, 4)))
      .catch(() => setProjects([]));
  }, []);

  return (
    <>
      {/* 1. Hero */}
      <Hero
        image={POSTER.product}
        video={VIDEO.product}
        eyebrow="PRODUCT & ENGINEERING"
        title="You have the idea. We build it."
        subline="Product direction, engineering, and applied AI — we scope it, build it, ship it, and then take it to market with you."
        primary={{ label: "Pitch us your idea", href: "#pitch" }}
        secondary={{ label: "See how we scope it →", href: "#how" }}
      />

      {/* 2. Who this is for */}
      <section className="bg-[var(--bg-base)] py-28 md:py-36" data-testid="product-who">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal><Label>Who this is for</Label></Reveal>
            <Reveal delay={80}><h2 className="mt-7 t-section">Ideas that deserve to exist.</h2></Reveal>
            <Reveal delay={140}>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
                People come to us with something they can see clearly — and no team to build it.
              </p>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {WHO.map((c, i) => (
              <Reveal key={c.t} delay={i * 90}>
                <div className="flex h-full flex-col rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-7 transition-transform duration-300 hover:-translate-y-1">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--ember)]/10 text-[var(--ember)]">
                    <c.icon size={22} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-6 text-lg font-semibold tracking-[-0.02em] text-[var(--ink)]">{c.t}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[var(--muted)]">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Beyond workflows */}
      <section className="bg-[var(--bg-contrast)] py-28 md:py-36" data-testid="product-distinction">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal><Label>This isn't automation</Label></Reveal>
          <Reveal delay={80}>
            <h2 className="mt-7 t-section">Workflows run your business. Products build a new one.</h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-5 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
              Our Workflow team automates the work you already do. This team creates something that didn't exist — and takes it to market.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <Link to="/workflow" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--ember)] hover:underline" data-testid="product-workflow-link">
              See Workflow &amp; Automation <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 4. What we build */}
      <section className="bg-[var(--bg-base)] py-28 md:py-36" data-testid="product-build">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal><Label>What we build</Label></Reveal>
            <Reveal delay={80}><h2 className="mt-7 t-section">From idea to shipped.</h2></Reveal>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BUILD.map((c, i) => (
              <Reveal key={c.t} delay={(i % 3) * 90}>
                <div className="flex h-full flex-col rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-7">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--ember)]/10 text-[var(--ember)]">
                    <c.icon size={20} strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-5 text-lg font-semibold tracking-[-0.02em] text-[var(--ink)]">{c.t}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[var(--muted)]">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Team leader */}
      <section className="bg-[var(--bg-contrast)] py-28 md:py-36" data-testid="product-leader">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 md:px-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-black/5">
              <img src={leader.photo} alt={leader.name} className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <Reveal><Label>Meet the team leader</Label></Reveal>
            <Reveal delay={80}><h2 className="mt-7 t-section">{leader.name}</h2></Reveal>
            <Reveal delay={140}>
              <p className="mt-2 text-sm font-semibold uppercase tracking-[0.1em] text-[var(--muted)]">
                {leader.title} · owns scoping, architecture, and the build
              </p>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted)]">{leader.bio}</p>
            </Reveal>
            <Reveal delay={260}>
              <div className="mt-8 flex items-start gap-3 rounded-2xl border border-[var(--ember)]/30 bg-[var(--ember)]/[0.05] p-6">
                <ShieldCheck size={20} className="mt-0.5 flex-shrink-0 text-[var(--ember)]" />
                <p className="text-[15px] leading-relaxed text-[var(--ink)]">
                  Every build has a named human accountable for it — that's AI Standards.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 7. Selected work */}
      {projects.length > 0 && (
        <section className="bg-[var(--charcoal)] py-28 text-white md:py-36" data-testid="product-work">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div className="max-w-2xl">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--mint)]">Selected work</span>
                <Reveal delay={80}>
                  <h2 className="mt-7 t-section text-white">Products we've built and shipped.</h2>
                </Reveal>
                <Reveal delay={140}>
                  <p className="mt-4 text-base leading-relaxed text-white/60 sm:text-lg">
                    A sample of the ideas we scoped, engineered, and put live with founders and teams.
                  </p>
                </Reveal>
              </div>
              <Reveal delay={160}>
                <Link to="/work" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--mint)] hover:underline" data-testid="product-work-link">
                  See the full portfolio <ArrowRight size={15} />
                </Link>
              </Reveal>
            </div>
            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {projects.map((p, i) => (
                <VideoTile key={p.slug} project={p} delay={(i % 2) * 90} aspect="aspect-[4/5]" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 6. How a build works */}
      <section id="how" className="scroll-mt-24 bg-[var(--bg-base)] py-28 md:py-36" data-testid="product-how">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <Reveal><Label>How it works</Label></Reveal>
            <Reveal delay={80}><h2 className="mt-7 t-section">We scope it before anyone commits.</h2></Reveal>
            <Reveal delay={140}>
              <p className="mt-4 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
                No packages here. Every build is scoped and proposed on its own terms.
              </p>
            </Reveal>
          </div>
          <div className="mt-16 grid gap-6 lg:grid-cols-4 lg:gap-5">
            {PHASES.map((p, i) => (
              <Reveal key={p.t} delay={i * 100}>
                <div className="flex h-full flex-col rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-7">
                  <span className="text-3xl font-semibold tracking-[-0.03em] text-[var(--ember)]/30">{p.n}</span>
                  <h3 className="mt-5 text-xl font-semibold tracking-[-0.02em] text-[var(--ink)]">{p.t}</h3>
                  <p className="mt-3 flex-1 text-[15px] leading-relaxed text-[var(--muted)]">{p.d}</p>
                  <span className="mt-5 w-fit rounded-full bg-[var(--ember)]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ember)]">
                    {p.tag}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Centerpiece — go-to-market handoff */}
      <section className="relative overflow-hidden bg-[var(--charcoal)] py-28 text-white md:py-36" data-testid="product-handoff">
        <div className="pointer-events-none absolute -right-24 top-1/4 h-72 w-72 rounded-full bg-[var(--mint)]/10 blur-[120px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-3xl">
            <Reveal><Label dark>What makes this different</Label></Reveal>
            <Reveal delay={80}>
              <h2 className="mt-7 t-section text-white">Most shops build it and hand you the keys.</h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-5 text-base leading-relaxed text-white/70 sm:text-lg">
                We can drive it off the lot. When the product is ready, the rest of the department is already here — the Growth team launches it, Workflow &amp; Automation runs the operation behind it. Build and go-to-market under one roof.
              </p>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {HANDOFF.map((c, i) => {
              const inner = (
                <div className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[var(--mint)]/40">
                  <p className="text-lg font-semibold tracking-[-0.01em] text-white">
                    {c.t}
                    {c.to && <ArrowRight size={16} className="ml-2 inline text-[var(--mint)]" />}
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-white/60">{c.d}</p>
                </div>
              );
              return (
                <Reveal key={c.t} delay={i * 90}>
                  {c.to ? (
                    <Link to={c.to} data-testid={`handoff-link-${c.to.slice(1)}`}>{inner}</Link>
                  ) : (
                    inner
                  )}
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={320}>
            <p className="mt-12 text-lg font-semibold tracking-tight text-[var(--mint)]">
              One idea. One department. From blank page to first customer.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 8. Proposal-based pricing note */}
      <section className="bg-[var(--bg-base)] py-28 md:py-36" data-testid="product-pricing-note">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal><Label>Pricing</Label></Reveal>
          <Reveal delay={80}><h2 className="mt-7 t-section">Custom work, priced honestly.</h2></Reveal>
          <Reveal delay={140}>
            <p className="mt-5 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
              Product builds don't fit a monthly package — so we don't pretend they do. Every project is scoped and proposed on its own: fixed scope, clear cost, no surprises.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <Link to="/pricing" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[var(--ember)] hover:underline" data-testid="product-pricing-link">
              Looking for our managed AI department instead? See pricing <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="bg-[var(--bg-contrast)] py-24 md:py-32" data-testid="product-faq">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <Reveal><Label>Questions</Label></Reveal>
          <Reveal delay={80}><h2 className="mt-7 t-section">Straight answers for founders.</h2></Reveal>
          <Reveal delay={140}>
            <Accordion type="single" collapsible className="mt-10">
              {FAQ.map(([q, a], i) => (
                <AccordionItem key={q} value={`faq-${i}`} className="border-black/10" data-testid={`product-faq-${i}`}>
                  <AccordionTrigger className="py-5 text-left text-lg font-semibold tracking-[-0.01em] text-[var(--ink)] hover:no-underline">
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-[var(--muted)]">
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* 10. Pitch form */}
      <section id="pitch" className="scroll-mt-24 bg-[var(--bg-base)] py-24 md:py-32" data-testid="product-pitch">
        <div className="mx-auto grid max-w-7xl items-start gap-16 px-6 md:px-12 lg:grid-cols-2">
          <div>
            <Reveal><Label>Pitch us</Label></Reveal>
            <Reveal delay={80}><h2 className="mt-7 t-section">Tell us what you want to build.</h2></Reveal>
            <Reveal delay={140}>
              <p className="mt-5 text-base leading-relaxed text-[var(--muted)] sm:text-lg">
                One call. We'll tell you honestly whether it's worth building — and what it would take.
              </p>
            </Reveal>
          </div>
          <Reveal delay={120}>
            <div className="rounded-3xl border border-black/5 bg-[var(--bg-contrast)] p-6 sm:p-10">
              <PitchForm />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 11. Closing CTA band */}
      <CtaBand variant="pitch" />
    </>
  );
}
